import {
  ActionIcon,
  Box,
  Divider,
  FileButton,
  Group,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core';
import {
  IconArrowUp,
  IconPaperclip,
  IconPlayerStopFilled,
  IconSparkles,
} from '@tabler/icons-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { chatFileAcceptHint, chatFileButtonAccept, ChatFileTypes } from '../chats/chatFileTypes';
import {
  buildSkillMenuRows,
  ChatSkill,
  composerPlaceholder,
  findSlashCommand,
  removeSlashCommand,
  skillMenuOptions,
  SlashCommandQuery,
} from '../chats/skillCommands';
import { ThinkingEffort } from '../chats/thinkingEffort';
import { useConversations } from '../chats/useConversations';
import { useHistoryScope } from '../chats/useHistoryScope';
import { ChatFile, UploadingChatFile } from '../chats/types';
import { SiestaMode } from '../types';
import { CHAT_BAR_TAB_LABELS } from './ChatModeTabs';
import { ChatFileList } from './ChatFileList';
import { SelectedSkillChip } from './SelectedSkillChip';
import { SKILL_COMMAND_MENU_ID, SkillCommandMenu, skillOptionId } from './SkillCommandMenu';
import { ThinkingEffortControl } from './ThinkingEffortControl';

const SKILL_CHIP_GAP = 8;
// Centres the chip on the first line of text, which starts below the input's own top padding.
const SKILL_CHIP_TOP = 9;

type Props = {
  conversationId: string;
  siestaMode: SiestaMode;
  pendingFiles: ChatFile[];
  uploadingFiles: UploadingChatFile[];
  fileTypes?: ChatFileTypes;
  onSubmit: (message: string) => void;
  onFocus?: () => void;
  isProcessing?: boolean;
  onStop?: () => void;
  onAttachFiles: (files: File[]) => void;
  onRemovePendingFile: (file: ChatFile) => void;
  isUploading: boolean;
  dense?: boolean;
  // The firm's skills the slash command can pick from; undefined hides the command entirely.
  skills?: ChatSkill[];
  selectedSkill?: ChatSkill | null;
  onSelectSkill?: (skill: ChatSkill | null) => void;
  // The effort for this chat's messages. The parent owns it: maximum until the adviser chooses
  // otherwise, and then what they chose for the rest of the chat. Undefined hides the control.
  thinkingEffort?: ThinkingEffort;
  onThinkingEffortChange?: (effort: ThinkingEffort) => void;
};

export function SiestaChatInput({
  conversationId,
  siestaMode,
  pendingFiles,
  uploadingFiles,
  fileTypes,
  onSubmit,
  onFocus,
  isProcessing,
  onStop,
  onAttachFiles,
  onRemovePendingFile,
  isUploading,
  dense,
  skills,
  selectedSkill = null,
  onSelectSkill,
  thinkingEffort,
  onThinkingEffortChange,
}: Props) {
  const [value, setValue] = useState('');
  const [slashCommand, setSlashCommand] = useState<SlashCommandQuery | null>(null);
  // The skills button opens the same list as `/`, for users who do not know the shortcut.
  const [menuOpenedByButton, setMenuOpenedByButton] = useState(false);
  // Null means no row is highlighted: Enter then sends the message as typed. The pinned create
  // row is never highlighted on its own, so "/tmp/foo" still sends for admins.
  const [activeSkillIndex, setActiveSkillIndex] = useState<number | null>(null);
  const [effortPanelOpened, setEffortPanelOpened] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const skillChipRef = useRef<HTMLDivElement>(null);
  const [skillChipWidth, setSkillChipWidth] = useState(0);
  const effortControlShown = thinkingEffort !== undefined && onThinkingEffortChange !== undefined;
  // Alongside the effort control the composer is the same on every tab: clicking into the box moves
  // to the conversation, and controls that only existed there would shoot into view under the cursor.
  const skillsAvailable =
    skills !== undefined &&
    onSelectSkill !== undefined &&
    (siestaMode === 'chat' || effortControlShown);
  const commandSkill = skillsAvailable ? selectedSkill : null;
  const menuRows = buildSkillMenuRows(skills ?? [], slashCommand?.query ?? '');
  const matchingSkills = skillMenuOptions(menuRows);
  const menuOpen = skillsAvailable && (slashCommand !== null || menuOpenedByButton);
  const skillsButtonVisible = skillsAvailable && (skills?.length ?? 0) > 0;
  // The skill list can refetch while the menu is open, so the highlight is clamped to what is
  // actually shown rather than trusting the stored index.
  const activeIndex =
    menuOpen && activeSkillIndex !== null && matchingSkills.length > 0
      ? Math.min(activeSkillIndex, matchingSkills.length - 1)
      : null;
  const activeSkill = activeIndex === null ? undefined : matchingSkills[activeIndex];
  const { clientId, familyId } = useHistoryScope();
  const { data: conversations = [] } = useConversations(clientId, familyId);
  const chatName =
    conversations.find(conversation => conversation.id === conversationId)?.title ?? null;
  const canAttach = siestaMode === 'chat';
  let attachTooltip = '';
  if (!canAttach) {
    attachTooltip = `Files can only be attached in the ${CHAT_BAR_TAB_LABELS.chat} tab`;
  } else if (fileTypes) {
    attachTooltip = chatFileAcceptHint(fileTypes);
  }

  // Put the cursor in the box on entering a chat, opening a conversation, or picking an action
  // (all of which land in chat mode), so the user can type without clicking in first.
  useEffect(() => {
    if (siestaMode === 'chat') {
      inputRef.current?.focus();
    }
  }, [siestaMode, conversationId]);

  // The level belongs to the chat, so a panel left open would be showing another chat's level
  // change under the adviser's hands.
  useEffect(() => {
    setEffortPanelOpened(false);
  }, [conversationId]);

  // The picked command sits at the start of the first line and the text is indented past it, so
  // the two read as one message and a long instruction wraps underneath the command.
  useLayoutEffect(() => {
    const chip = skillChipRef.current;
    if (!chip) {
      setSkillChipWidth(0);
      return undefined;
    }
    const measure = () => setSkillChipWidth(chip.offsetWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(chip);
    return () => observer.disconnect();
  }, [commandSkill]);

  const trimmedValue = value.trim();
  // A command is a complete message on its own; the instruction after it is optional.
  const canSend = Boolean(trimmedValue) || commandSkill !== null;

  const handleSubmit = () => {
    const trimmed = trimmedValue;
    if (!isProcessing && !isUploading && canSend) {
      onSubmit(trimmed);
      setValue('');
      setEffortPanelOpened(false);
      setSlashCommand(null);
      setMenuOpenedByButton(false);
    }
  };

  const updateSlashCommand = (nextValue: string, caret: number) => {
    if (!skillsAvailable) return;
    const next = findSlashCommand(nextValue, caret);
    const changed =
      next === null || slashCommand === null
        ? next !== slashCommand
        : next.query !== slashCommand.query || next.start !== slashCommand.start;
    if (changed) {
      const nextRows = next ? buildSkillMenuRows(skills ?? [], next.query) : null;
      setActiveSkillIndex(nextRows && nextRows.matches.length > 0 ? 0 : null);
    }
    setSlashCommand(next);
  };

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value: nextValue, selectionStart } = event.currentTarget;
    setValue(nextValue);
    setMenuOpenedByButton(false);
    updateSlashCommand(nextValue, selectionStart ?? nextValue.length);
  };

  const toggleSkillMenu = () => {
    if (menuOpen) {
      setSlashCommand(null);
      setMenuOpenedByButton(false);
      return;
    }
    setMenuOpenedByButton(true);
    setActiveSkillIndex(menuRows.matches.length > 0 ? 0 : null);
    inputRef.current?.focus();
  };

  const handleSelectSkill = (skill: ChatSkill) => {
    if (!onSelectSkill) return;
    onSelectSkill(skill);
    const next = slashCommand ? removeSlashCommand(value, slashCommand) : null;
    if (next) setValue(next.value);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      if (next) inputRef.current?.setSelectionRange(next.caret, next.caret);
    });
    setSlashCommand(null);
    setMenuOpenedByButton(false);
  };

  const handleMenuKeyDown = (event: React.KeyboardEvent): boolean => {
    if (!menuOpen) return false;
    if (event.key === 'Escape') {
      setSlashCommand(null);
      setMenuOpenedByButton(false);
      return true;
    }
    if (matchingSkills.length === 0) return false;
    if (event.key === 'ArrowDown') {
      setActiveSkillIndex(activeIndex === null ? 0 : (activeIndex + 1) % matchingSkills.length);
      return true;
    }
    if (event.key === 'ArrowUp') {
      setActiveSkillIndex(
        activeIndex === null
          ? matchingSkills.length - 1
          : (activeIndex - 1 + matchingSkills.length) % matchingSkills.length,
      );
      return true;
    }
    if (((event.key === 'Enter' && !event.shiftKey) || event.key === 'Tab') && activeSkill) {
      handleSelectSkill(activeSkill);
      return true;
    }
    return false;
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.nativeEvent.isComposing) return;
    if (handleMenuKeyDown(event)) {
      event.preventDefault();
      return;
    }
    const { selectionStart, selectionEnd } = event.currentTarget;
    if (event.key === 'Backspace' && commandSkill && selectionStart === 0 && selectionEnd === 0) {
      event.preventDefault();
      onSelectSkill?.(null);
      return;
    }
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmit();
    }
  };

  const handleSelect = (event: React.SyntheticEvent<HTMLTextAreaElement>) => {
    const { value: currentValue, selectionStart } = event.currentTarget;
    updateSlashCommand(currentValue, selectionStart ?? currentValue.length);
  };

  return (
    <Box
      c="white"
      px="xl"
      pt="md"
      pb={dense ? 'md' : 'xxxl'}
      style={{
        borderTop: '1px solid rgba(255, 255, 255, 0.12)',
        borderTopLeftRadius: 20,
        borderTopRightRadius: 20,
      }}
    >
      {canAttach && (
        <ChatFileList
          files={pendingFiles}
          uploadingFiles={uploadingFiles}
          onRemove={onRemovePendingFile}
        />
      )}

      {menuOpen && (
        <SkillCommandMenu
          rows={menuRows}
          activeIndex={activeIndex}
          onHover={setActiveSkillIndex}
          onSelect={handleSelectSkill}
        />
      )}

      {effortControlShown && (
        <ThinkingEffortControl
          effort={thinkingEffort}
          onChange={onThinkingEffortChange}
          opened={effortPanelOpened}
          onOpenedChange={setEffortPanelOpened}
        />
      )}

      <Box pos="relative">
        {commandSkill && (
          <Box
            ref={skillChipRef}
            pos="absolute"
            top={SKILL_CHIP_TOP}
            left={0}
            maw="70%"
            style={{ zIndex: 1, display: 'flex' }}
          >
            <SelectedSkillChip skill={commandSkill} onRemove={() => onSelectSkill?.(null)} />
          </Box>
        )}

        <Textarea
          ref={inputRef}
          variant="unstyled"
          size="md"
          placeholder={composerPlaceholder(skillsAvailable, commandSkill)}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onSelect={handleSelect}
          onFocus={onFocus}
          onBlur={() => setMenuOpenedByButton(false)}
          role={menuOpen ? 'combobox' : undefined}
          aria-expanded={menuOpen ? true : undefined}
          aria-controls={menuOpen ? SKILL_COMMAND_MENU_ID : undefined}
          aria-activedescendant={activeSkill ? skillOptionId(activeSkill) : undefined}
          autosize
          minRows={1}
          maxRows={dense ? 4 : 6}
          styles={{
            input: {
              border: 'none',
              paddingLeft: 0,
              paddingBottom: 0,
              color: 'var(--mantine-color-white)',
              '--input-placeholder-color': 'var(--mantine-color-white)',
              textIndent: skillChipWidth > 0 ? skillChipWidth + SKILL_CHIP_GAP : undefined,
            },
          }}
        />
      </Box>

      <Divider mt={2} mb="sm" color="gray.4" />

      <Group justify="space-between" align="center" wrap="nowrap">
        <Group gap="xs" align="center" wrap="nowrap">
          <Tooltip label={attachTooltip} disabled={!attachTooltip} position="top" withArrow>
            <Box style={{ display: 'inline-flex' }}>
              <FileButton
                multiple
                onChange={onAttachFiles}
                accept={fileTypes ? chatFileButtonAccept(fileTypes.extensions) : undefined}
              >
                {props => (
                  <ActionIcon
                    {...props}
                    variant="filled"
                    color="gray"
                    size="lg"
                    aria-label="Attach file"
                    loading={isUploading}
                    disabled={!canAttach}
                    style={{ borderRadius: '50%' }}
                  >
                    <IconPaperclip size={18} />
                  </ActionIcon>
                )}
              </FileButton>
            </Box>
          </Tooltip>

          {skillsButtonVisible && (
            <Tooltip label="Use a skill" position="top" withArrow>
              <ActionIcon
                variant="filled"
                color="gray"
                size="lg"
                aria-label="Use a skill"
                aria-expanded={menuOpen}
                aria-controls={menuOpen ? SKILL_COMMAND_MENU_ID : undefined}
                // Keep focus in the textarea so the list can be driven with the arrow keys
                // and a click does not close it through the textarea's blur.
                onMouseDown={event => event.preventDefault()}
                onClick={toggleSkillMenu}
                style={{ borderRadius: '50%' }}
              >
                <IconSparkles size={18} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>

        {chatName && (
          <Text flex={1} ta="center" size="sm" fw={FONT_WEIGHTS.medium} lineClamp={1}>
            {`Chat: ${chatName}`}
          </Text>
        )}

        {isProcessing ? (
          <ActionIcon
            variant="filled"
            color="gray"
            size="lg"
            aria-label="Stop generating"
            onClick={onStop}
            style={{ borderRadius: '50%' }}
          >
            <IconPlayerStopFilled size={18} />
          </ActionIcon>
        ) : (
          <ActionIcon
            variant="filled"
            color="gray"
            size="lg"
            aria-label="Send message"
            onClick={handleSubmit}
            disabled={!canSend || isUploading}
            style={{ borderRadius: '50%' }}
          >
            <IconArrowUp size={18} />
          </ActionIcon>
        )}
      </Group>
    </Box>
  );
}
